import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { supabase } from "../../services/supabase";

const businessNavItems = [
  {
    name: "Dashboard",
    path: "/business/seller-dashboard",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    name: "Profile",
    path: "/business/profile",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 15c2.485 0 4.797.657 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    name: "Tyre Inventory",
    path: "/business/inventory",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        <circle cx="12" cy="12" r="4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
    ),
  },
  {
    name: "Rim Inventory",
    path: "/business/rim-inventory",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v6M12 15v6M21 12h-6M9 12H3" />
      </svg>
    ),
  },
  {
    name: "Analytics",
    path: "/business/analytics",
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17a2 2 0 104 0 2 2 0 00-4 0zm-7-2a2 2 0 104 0 2 2 0 00-4 0zm14-6a2 2 0 104 0 2 2 0 00-4 0zm-7-2a2 2 0 104 0 2 2 0 00-4 0z" />
      </svg>
    ),
  },
   {
     name: "Reviews",
     path: "/business/reviews",
     icon: (
       <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
       </svg>
     ),
   },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useAuthStore();
  const [businessSlug, setBusinessSlug] = useState<string | null>(null);

  // Fetch business slug for the logged-in user
  useEffect(() => {
    const fetchBusinessSlug = async () => {
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('businesses')
        .select('slug')
        .eq('owner_id', user.id)
        .maybeSingle();
      
      if (data && !error) {
        setBusinessSlug(data.slug);
      }
    };

    fetchBusinessSlug();
  }, [user?.id]);

  return (
    <nav
      className={`h-full bg-white border-r flex flex-col py-6 px-2 shadow-sm transition-all duration-300 ${
        collapsed ? "w-20" : "w-64"
      }`}
      aria-label="Sidebar Navigation"
    >
      <div
        className={`mb-8 flex items-center ${collapsed ? "justify-center" : "justify-between"} px-4`}
        style={{ height: "3.6rem" }}
      >
        {!collapsed && (
          <img
            src="/logo.svg"
            alt="Site Logo"
            className="object-contain mx-auto"
            style={{
              width: "80px",
              height: "auto",
              transition: "width 0.3s"
            }}
          />
        )}
        <button
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`p-2 rounded hover:bg-gray-100 transition ${collapsed ? "mx-auto" : ""}`}
          onClick={() => setCollapsed((c) => !c)}
        >
          <svg
            className="w-6 h-6 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {collapsed ? (
              // >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            ) : (
              // <
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            )}
          </svg>
        </button>
      </div>
      <ul className="flex-1 space-y-2">
        {businessNavItems.map((item) => (
          <li key={item.name}>
            <Link
              to={item.path}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors relative ${
                location.pathname === item.path
                  ? "bg-blue-100 text-blue-700"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
              tabIndex={0}
              aria-label={item.name}
            >
              <div className="relative">
                {item.icon}
              </div>
              {!collapsed && (
                <div className="ml-3 flex items-center justify-between w-full">
                  <span>{item.name}</span>
                </div>
              )}
            </Link>
          </li>
        ))}
        
        {/* View Shop Link - Opens in new tab */}
        {businessSlug && (
          <li className="pt-4 border-t border-gray-200">
            <a
              href={`/business/${businessSlug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              aria-label="View Shop (opens in new tab)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {!collapsed && (
                <div className="ml-3 flex items-center gap-2">
                  <span>View Shop</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              )}
            </a>
          </li>
        )}
      </ul>
    </nav>
  );
};

export default Sidebar;
